import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Calendar } from 'lucide-react';
import { quranApi } from '../services/api';
import { Button } from './ui/button';

export const VerseOfTheDay = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    quranApi
      .getVerseOfTheDay()
      .then((res) => {
        if (mounted) setData(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (loading) {
    return (
      <section className="py-16 px-4" data-testid="verse-of-the-day-loading">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-12 animate-pulse">
            <div className="h-4 w-24 bg-surface-elevated rounded mb-6" />
            <div className="h-12 w-full bg-surface-elevated rounded mb-4" />
            <div className="h-4 w-3/4 bg-surface-elevated rounded" />
          </div>
        </div>
      </section>
    );
  }

  if (!data?.verse) return null;
  const { verse, surah } = data;

  return (
    <section className="py-16 px-4" data-testid="verse-of-the-day-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative glass-card p-8 sm:p-12 overflow-hidden"
          data-testid="verse-of-the-day-card"
        >
          {/* Decorative gradient */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="overline block">Verset du jour</span>
                  <p className="text-xs text-text-muted flex items-center gap-1 mt-0.5 capitalize">
                    <Calendar className="w-3 h-3" />
                    {today}
                  </p>
                </div>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary">
                {surah?.name_french || surah?.name_english} · {verse.surah_number}:{verse.verse_number}
              </span>
            </div>

            {/* Arabic */}
            <p
              className="font-arabic text-3xl sm:text-4xl lg:text-5xl leading-[2.2] text-right text-white mb-8"
              dir="rtl"
              data-testid="verse-of-the-day-arabic"
            >
              {verse.arabic_text}
            </p>

            {/* French translation */}
            {verse.translation_french && (
              <p
                className="text-lg text-text-primary leading-relaxed mb-3 italic"
                data-testid="verse-of-the-day-french"
              >
                « {verse.translation_french} »
              </p>
            )}

            {/* English subtitle */}
            {verse.translation_english && (
              <p className="text-sm text-text-muted leading-relaxed mb-8">
                {verse.translation_english}
              </p>
            )}

            {/* CTA */}
            <div className="flex items-center justify-between flex-wrap gap-3 pt-6 border-t border-border/60">
              <div className="flex items-center gap-2 font-arabic text-primary text-xl" dir="rtl">
                {surah?.name_arabic}
              </div>
              <Link to={`/quran/${verse.surah_number}`} data-testid="verse-of-the-day-cta">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/50 text-primary hover:bg-primary/10"
                >
                  Lire la sourate
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VerseOfTheDay;
